import Button from 'antd/lib/button'
import Collapse from 'antd/lib/collapse'
import Divider from 'antd/lib/divider'
import Form from 'antd/lib/form'
import { clsx } from 'shared/utils/react.utils'
import { useEffect, useRef, useState } from 'react'
import { isNil } from 'lodash'
import { AttributesData, TaskAttribute } from 'Types/Tasks.types'
import { AttributeValueType } from 'Types/Attribute.types'
import { ClassLevelAttributesPanelProps } from 'Types/Classes.types'
import { AttributesContent } from 'Components/tasks/Attributes/AtributesContent'
import { getDefaultAttributeValue, showAttribute } from 'Utils/attributes.utils.ts'
import CheckCircleFilledIcon from 'src/assets/icons/check-circle-filled.svg?react'
import IncompleteIcon from 'src/assets/icons/incomplete.svg?react'
import styles from './Classes.module.less'

const mergeFormValuesWithStored = (
    formValues: Record<string, unknown>,
    storedValues: AttributesData | undefined,
    attributes: TaskAttribute[]
): AttributesData => {
    const merged = { ...(storedValues ?? {}) } as AttributesData
    attributes.forEach(attr => {
        const formValue = formValues[attr.name]
        if (!isNil(formValue)) {
            merged[attr.name] = {
                data: {
                    value: formValue as AttributeValueType,
                    value_type: attr.value_definition.value_type,
                },
            } as AttributesData[string]
        }
    })
    return merged
}

export const ClassLevelAttributesPanel = (props: ClassLevelAttributesPanelProps) => {
    const toUpdateRef = useRef<Record<string, AttributeValueType | undefined>>({})
    const valuesForVisibility = mergeFormValuesWithStored({}, props.values, props.attributes)
    const [isPreviewExpanded, setIsPreviewExpanded] = useState(false)
    const [isPreviewOverflowing, setIsPreviewOverflowing] = useState(false)
    const previewId = `class-level-card-preview-${props.annotationId}`

    useEffect(() => {
        Object.entries(toUpdateRef.current).forEach(([name, value]) => {
            props.onUpdateAttribute(name, value)
        })
        toUpdateRef.current = {}
    })

    useEffect(() => {
        setIsPreviewExpanded(false)
    }, [props.textPreview, props.annotationId])

    useEffect(() => {
        if (!props.textPreview || isPreviewExpanded) return
        const previewElement = document.getElementById(previewId)
        if (!previewElement) return

        const checkOverflow = () => {
            setIsPreviewOverflowing(previewElement.scrollHeight > previewElement.clientHeight + 1)
        }

        checkOverflow()

        const observer = new ResizeObserver(checkOverflow)
        observer.observe(previewElement)

        return () => observer.disconnect()
    }, [props.textPreview, isPreviewExpanded, previewId])

    const header = (
        <div
            className={styles['class-level-card-header-content']}
            onMouseEnter={() => props.onHoverAnnotation?.(props.annotationId)}
            onMouseLeave={() => props.onHoverAnnotation?.(null)}
        >
            <div className={styles['class-level-card-title-row']}>
                <div
                    className={styles['class-level-card-color']}
                    style={{ backgroundColor: props.classColor }}
                />
                <span className={styles['class-level-card-class-name']}>{props.classLabel}</span>
                {props.isIncomplete && (
                    <span className={styles['class-level-incomplete-badge']}>*</span>
                )}
                <div className={styles['class-level-card-status-icon']}>
                    {props.isIncomplete ? (
                        <IncompleteIcon />
                    ) : (
                        <CheckCircleFilledIcon
                            className={styles['class-level-check-circle-icon']}
                        />
                    )}
                </div>
            </div>
            {props.textPreview ? (
                <>
                    <Divider className={styles['class-level-card-divider']} />
                    <div
                        id={previewId}
                        className={clsx(
                            styles['class-level-card-preview'],
                            isPreviewExpanded
                                ? styles['class-level-card-preview-expanded']
                                : undefined
                        )}
                    >
                        {props.textPreview}
                        {!isPreviewExpanded && isPreviewOverflowing && (
                            <Button
                                type="link"
                                className={styles['class-level-card-preview-toggle-inline']}
                                onClick={event => {
                                    event.stopPropagation()
                                    setIsPreviewExpanded(true)
                                }}
                            >
                                See more
                            </Button>
                        )}
                    </div>
                    {isPreviewOverflowing && isPreviewExpanded && (
                        <Button
                            type="link"
                            className={styles['class-level-card-preview-toggle-below']}
                            onClick={event => {
                                event.stopPropagation()
                                setIsPreviewExpanded(false)
                            }}
                        >
                            See less
                        </Button>
                    )}
                </>
            ) : null}
        </div>
    )

    return (
        <Collapse
            expandIconPosition="end"
            {...(props.isIncomplete && props.highlightIncomplete
                ? { activeKey: [props.annotationId] }
                : {})}
            className={clsx(
                styles['class-level-accordion'],
                props.isIncomplete && props.highlightIncomplete
                    ? styles['class-level-accordion-highlighted']
                    : undefined
            )}
            items={[
                {
                    key: props.annotationId,
                    label: header,
                    forceRender: true,
                    children: (
                        <div className={styles['class-level-card-body']}>
                            <Form form={props.form} layout="vertical" disabled={props.isReadOnly()}>
                                {props.attributes.map(attribute => {
                                    if (
                                        !showAttribute(
                                            props.attributes,
                                            attribute,
                                            props.attributesOnUI,
                                            valuesForVisibility,
                                            toUpdateRef
                                        )
                                    ) {
                                        delete props.attributesOnUI[attribute.name]
                                        return null
                                    }
                                    props.attributesOnUI[attribute.name] = 1
                                    return (
                                        <AttributesContent
                                            key={attribute.attribute_id + attribute.name}
                                            attribute={attribute}
                                            value={
                                                props.values?.[attribute.name]?.data?.value ??
                                                getDefaultAttributeValue(
                                                    attribute.value_definition.value_type
                                                )
                                            }
                                            onUpdateAttribute={(name, value) => {
                                                props.onUpdateAttribute(name, value)
                                            }}
                                            isReadOnly={props.isReadOnly}
                                        />
                                    )
                                })}
                            </Form>
                        </div>
                    ),
                },
            ]}
        />
    )
}
